import { currentVehicleAtom } from '@core/Atoms/Vehicle/CurrentVehicle.atom';
import { Locale } from '@core/Entities/Locale/Locale.entity';

import { getFormattedCurrency } from '@core/Utils/Intl/GetFormattedCurrency';
import { Typography } from '@gds/Typography/Typography';
import { useAtomValue } from 'jotai';

import { Calculation, CalculationProduct } from '../../../Entities/Calculation.entity';
import { isLeasingFinancing, isStandardFinancing } from '../../../Utils/isFinanceCaptive';
import { getValueFormatterForProduct } from '../../../Utils/getValueFormatterForProduct';

import { CalculatorMessages } from '../Calculator.messages';
import { isCgi, isVwfs } from '../Helpers/FinanceMap';
import InsuranceSummary from '../InsuranceSummary/InsuranceSummary';
import { LegalDisclaimers } from '../LegalDisclaimers/LegalDisclaimers';

import styles from './Summary.module.css';

type SummaryFRProps = {
  calculation: Calculation;
  product: CalculationProduct;
  locale: Locale;
  summaryStrings: CalculatorMessages;
};

export const SummaryFR = ({ product, locale, summaryStrings, calculation }: SummaryFRProps) => {
  const getSelectedAdditionalServices =
    product?.additionalServices?.filter(service => service.userSelected) || [];
  const captiveId = calculation?.captiveId;
  const vehicle = useAtomValue(currentVehicleAtom);
  const isCurrentCgi = isCgi(captiveId);
  const isCurrentVwfs = isVwfs(captiveId);
  const isCgiFinanceEligible = isCurrentCgi && locale === 'fr';
  const insuranceTitleInfoBubble = vehicle?.finance?.legalText?.secondaryCalculationDisclaimer;
  const insuranceTotalMonthly = vehicle?.finance?.additionalText?.totalMonthlyRate;
  const primaryDisclaimer = product?.offerPackageLegalText || '';
  const hasStandardFinancing = isStandardFinancing(calculation);
  const hasLeasingFinancing = isLeasingFinancing(calculation);
  const hasVwfsLeasing = hasLeasingFinancing && isCurrentVwfs;

  const valueFormatter = getValueFormatterForProduct(captiveId);

  const summaryComponent = (
    <summary className={styles.container}>
      <ul className={styles.summary}>
        <li data-id="instalments-container" className={styles.instalmentsContainer}>
          <ul className={styles.instalmentsList}>
            <li data-id="instalments" className={styles.instalments}>
              <span>{summaryStrings.totalMonthlyRate}</span>

              <span className={styles.totalMonthlyRate} data-test-id="calculationMonthlyRate">
                {getFormattedCurrency(
                  valueFormatter(product.calculationResult.totalMonthlyRate),
                  locale,
                )}{' '}
                {summaryStrings.totalUniversalMonthlyAsterik}
                {isCgiFinanceEligible && (
                  <span className={styles.infoIcon}>
                    <LegalDisclaimers
                      header={summaryStrings.subAdditionalServices}
                      content={primaryDisclaimer}
                    />
                  </span>
                )}
              </span>
            </li>
          </ul>
        </li>

        <li data-id="fees">
          <ul>
            <li>
              <span>
                {hasVwfsLeasing ? summaryStrings.totalDepositVwfsFr : summaryStrings.totalDeposit}
              </span>
              <Typography Component="span" variant="body2" data-test-id="calculationDeposit">
                {getFormattedCurrency(valueFormatter(product.inputs.deposit.value), locale)}
              </Typography>
            </li>
            <li>
              <span>{summaryStrings.vehiclePrice}</span>
              <Typography Component="span" variant="body2" data-test-id="calculationVehiclePrice">
                {getFormattedCurrency(vehicle?.pricing?.price, locale)}
              </Typography>
            </li>

            {!hasVwfsLeasing && (
              <li>
                <span>{summaryStrings.totalUniversalVehiclePrice}</span>
                <Typography Component="span" variant="body2" data-test-id="calculationTotalAmount">
                  {getFormattedCurrency(
                    valueFormatter(product.calculationResult.totalAmount),
                    locale,
                  )}
                </Typography>
              </li>
            )}

            {hasStandardFinancing && (
              <li>
                <span>{summaryStrings.taeg}</span>
                <Typography
                  Component="span"
                  variant="body2"
                  data-test-id="calculationAnnualInterestRate"
                >
                  {product.calculationResult.annualInterestRate}%
                </Typography>
              </li>
            )}
          </ul>
        </li>

        {getSelectedAdditionalServices.length > 0 && (
          <InsuranceSummary
            getSelectedAdditionalServices={getSelectedAdditionalServices}
            locale={locale}
            summaryStrings={summaryStrings}
            insuranceTitleInfoBubble={insuranceTitleInfoBubble}
            insuranceTotalMonthly={insuranceTotalMonthly}
            product={product}
          />
        )}
      </ul>
    </summary>
  );
  return summaryComponent;
};
